import { forwardRef, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const cardVariants = cva(
  "rounded-xl border transition-colors",
  {
    variants: {
      variant: {
        default: "bg-daydra-surface border-daydra-border",
        raised: "bg-daydra-raised border-daydra-border",
        ghost: "bg-transparent border-transparent",
        accent: "bg-daydra-accent-ghost border-daydra-border-focus",
        success: "bg-daydra-success-dim border-daydra-success/20",
        error: "bg-daydra-error-dim border-daydra-error/20",
        warning: "bg-daydra-warning-dim border-daydra-warning/20",
        info: "bg-daydra-info-dim border-daydra-info/20",
      },
      padding: {
        none: "",
        sm: "p-3",
        default: "p-4",
        lg: "p-5",
      },
      interactive: {
        true: "cursor-pointer hover:border-daydra-border-hover active:scale-[0.995]",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      padding: "default",
      interactive: false,
    },
  },
);

export interface CardProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, interactive, ...props }, ref) => {
    return (
      <div
        className={cn(cardVariants({ variant, padding, interactive, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);

Card.displayName = "Card";

/* Card subcomponents for structured layouts */

export const CardHeader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-3 pb-3", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<
  HTMLHeadingElement,
  HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn("text-sm font-semibold text-daydra-text", className)}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

export const CardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-daydra-dim", className)}
    {...props}
  />
));
CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-2 pt-3 border-t border-daydra-border", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";
